import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { classes, ClassItem } from "@/data/classes";
import ClassCard from "@/components/ClassCard";
import ClassDetailDialog from "@/components/ClassDetailDialog";
import { useDbClasses } from "@/hooks/useDbClasses";
import { useClassRegistry, slugOf } from "@/lib/classRegistry";
import { migratedSlugs } from "@/data/migrationFlags";
import { supabase } from "@/integrations/supabase/client";
import { toPublicClasses } from "@/lib/publicSessions";
import { publicStatusFor } from "@/lib/publicVisibility";
import { applyDisplayOrder, isNewAndEnrolling, useClassRanking } from "@/lib/publicClassOrder";


const Classes = () => {
  const dbClasses = useDbClasses();
  const registry = useClassRegistry();
  const ranking = useClassRanking();
  const [selected, setSelected] = useState<ClassItem | null>(null);
  const [filter, setFilter] = useState("All");
  const [optionCounts, setOptionCounts] = useState<Record<string, number>>({});
  const [params] = useSearchParams();

  useEffect(() => {
    (async () => {
      const { data } = await (supabase as any)
        .from("class_schedule_options")
        .select("class_id,status");
      if (!Array.isArray(data)) return;
      const counts: Record<string, number> = {};
      for (const row of data) {
        if (row.status === "hidden") continue;
        counts[row.class_id] = (counts[row.class_id] ?? 0) + 1;
      }
      setOptionCounts(counts);
    })();
  }, []);

  const visibleClasses = useMemo(() => {
    // Registry is the sole authority for public visibility and lifecycle
    // presentation; the mapping itself lives in `publicVisibility` so the
    // Classes page, Calendar, cards, and dialog can never disagree.
    const applyRegistryStatus = (c: ClassItem): ClassItem => {
      const slug = slugOf(c);
      const status = publicStatusFor(registry.statusOf(slug), {
        registrationEnabled: registry.isRegistrationEnabled(slug),
        fallback: c.status,
      });
      return status === c.status ? c : { ...c, status };
    };

    // Phase D: migrated slugs are DB-only. Drop their static counterparts before dedupe.
    const publishedStatic = classes
      .filter((c) => !migratedSlugs.has(slugOf(c)))
      .filter((c) => registry.isPublished(slugOf(c)))
      .map(applyRegistryStatus);
    const publishedDb = dbClasses.filter((c) => registry.isPublished(slugOf(c))).map(applyRegistryStatus);
    // Canonical dedupe by stable slug (never by title). If a static entry shares
    // its slug with a DB row (should not happen once migratedSlugs is complete),
    // the DB row wins.
    const dbSlugs = new Set(publishedDb.map((c) => slugOf(c)));
    const staticFiltered = publishedStatic.filter((c) => !dbSlugs.has(slugOf(c)));
    // Past sessions (and classes whose sessions have all finished) never render publicly.
    return toPublicClasses([...staticFiltered, ...publishedDb]);
  }, [dbClasses, registry.entries]);
  const categories = useMemo(
    () => ["All", ...Array.from(new Set(visibleClasses.map((c) => c.category)))],
    [visibleClasses],
  );

  const featuredOrder = ["beginners-mechanics", "all-about-farming", "mommy-me-crochet", "chess-club", "photography-for-teens"];
  const curatedOrder = [
    ...featuredOrder.map((id) => visibleClasses.find((c) => c.id === id)).filter((c): c is ClassItem => !!c),
    ...visibleClasses.filter((c) => !featuredOrder.includes(c.id)),
  ];
  // Live display ranking: still-filling classes first, then open classes, then
  // free/special-model offerings, then Coming Soon by parent interest. The
  // curated order above remains the final tie-breaker.
  const orderedClasses = useMemo(
    () => applyDisplayOrder(curatedOrder, ranking),
    [curatedOrder, ranking],
  );
  const filtered = filter === "All" ? orderedClasses : orderedClasses.filter((c) => c.category === filter);

  // Deep-link: ?class=<slug>[&option=<id>] auto-opens the class dialog so a
  // shared "Potential Future Sessions" link lands on the right card.
  useEffect(() => {
    const wantedSlug = params.get("class") ?? params.get("open");
    if (!wantedSlug || selected) return;
    const match = visibleClasses.find((c) => slugOf(c) === wantedSlug);
    if (match) setSelected(match);
  }, [params, visibleClasses, selected]);


  return (
    <div className="pt-24 pb-16 px-4">
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl font-bold text-foreground mb-3">Our Classes</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Deep dive, passion-driven classes for homeschoolers ages 6–18. Pick one or pick five!
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-full text-sm font-body font-semibold transition-colors ${
                filter === cat
                  ? "bg-primary text-primary-foreground"
                  : "bg-card text-muted-foreground hover:bg-accent"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((c) => (
            <ClassCard
              key={c.id}
              classItem={c}
              onClick={setSelected}
              additionalOptionsCount={c.dbId ? optionCounts[c.dbId] ?? 0 : 0}
              highlightNewAndEnrolling={isNewAndEnrolling(c, ranking)}
            />
          ))}
        </div>
      </div>

      <ClassDetailDialog classItem={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </div>
  );
};

export default Classes;
